import { useCallback, useEffect, useRef, useState } from 'react';
import { RoomRole, type GuestDays, type RoomMemberSummary } from 'minka-shared';
import roomAPI from '@/services/room-api';
import { describeAdminError } from '@/utils/api-error';
import { onRoomRoleChange } from '@/utils/room-role-events';

export interface UseRoomMembersReturn {
  members: RoomMemberSummary[];
  isLoading: boolean;
  /** The last failed mutation or load, already phrased for the user. */
  error: string | null;
  /** The member a mutation is currently running for, for per-row spinners. */
  pendingUserId: string | null;
  reload: () => Promise<void>;
  changeRole: (userId: string, role: RoomRole) => Promise<void>;
  extendGuest: (userId: string, days: GuestDays) => Promise<void>;
  removeMember: (userId: string) => Promise<void>;
  clearError: () => void;
}

/**
 * The island's members and the four things an admin does to them (§6.4).
 *
 * Every mutation reloads rather than patching local state: the server may have
 * changed more than the row that was clicked — a promotion moves who the last
 * active admin is, which is what disables the controls on another row.
 */
export function useRoomMembers(roomId: string | null | undefined): UseRoomMembersReturn {
  const [members, setMembers] = useState<RoomMemberSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  // Guards against a slow answer for a previous island landing on this one.
  const requestedRoomRef = useRef<string | null>(null);

  const reload = useCallback(async () => {
    if (!roomId) {
      setMembers([]);
      return;
    }

    requestedRoomRef.current = roomId;
    setIsLoading(true);

    try {
      const result = await roomAPI.getRoomMembers(roomId);
      if (requestedRoomRef.current === roomId) {
        setMembers(result);
        setError(null);
      }
    } catch (loadError) {
      if (requestedRoomRef.current === roomId) {
        setError(describeAdminError(loadError, 'Failed to load island members'));
      }
    } finally {
      if (requestedRoomRef.current === roomId) {
        setIsLoading(false);
      }
    }
  }, [roomId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Somebody else's change to this island is as good a reason to refresh as our
  // own: the last-active-admin rule depends on rows we did not touch.
  useEffect(() => onRoomRoleChange(roomId, () => void reload()), [roomId, reload]);

  const runMutation = useCallback(
    async (userId: string, mutate: () => Promise<void>) => {
      setPendingUserId(userId);
      setError(null);

      try {
        await mutate();
        await reload();
      } catch (mutationError) {
        // The server's prose can be as terse as "Conflict"; the machine-readable
        // code is what says *which* rule refused the change (§3.1, §3.2).
        setError(describeAdminError(mutationError, 'That change was refused'));
        throw mutationError;
      } finally {
        setPendingUserId(null);
      }
    },
    [reload]
  );

  const changeRole = useCallback(
    (userId: string, role: RoomRole) =>
      runMutation(userId, () => roomAPI.updateMemberRole(roomId!, userId, role)),
    [roomId, runMutation]
  );

  const extendGuest = useCallback(
    (userId: string, days: GuestDays) =>
      runMutation(userId, () => roomAPI.extendGuest(roomId!, userId, days)),
    [roomId, runMutation]
  );

  const removeMember = useCallback(
    (userId: string) => runMutation(userId, () => roomAPI.removeMember(roomId!, userId)),
    [roomId, runMutation]
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    members,
    isLoading,
    error,
    pendingUserId,
    reload,
    changeRole,
    extendGuest,
    removeMember,
    clearError
  };
}
